import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  Link as MuiLink,
  Alert,
  useTheme,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { useThemeMode } from '../../shared/contexts/ThemeContext';
import { useCookieConsent } from './CookieConsentContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { getCardStyles, getPrimaryButtonStyles, getSecondaryButtonStyles } from '../../shared/styles';
import { isAnalyticsConfigured } from '../../configuration/analytics';
import { SEO } from './SEO';
import { Section, PageHeader } from './PageSection';

const contentUseCase = new ContentUseCase();

const COPY = {
  de: {
    title: 'Cookie-Einstellungen',
    intro:
      'Hier legen Sie fest, was diese Website in Ihrem Browser speichern darf. Ihre Auswahl gilt sofort und lässt sich jederzeit wieder ändern.',
    sectionTitle: 'Was gespeichert werden darf',
    languageLabel: 'Sprachwahl merken',
    languageHint: 'Speichert Ihre zuletzt gewählte Sprache, damit die Startseite Sie dorthin führt.',
    themeLabel: 'Ansicht merken (hell/dunkel)',
    themeHint: 'Speichert, ob Sie die helle oder die dunkle Ansicht bevorzugen.',
    analyticsLabel: 'Anonyme Nutzungsstatistik (Google Analytics)',
    analyticsHint:
      'Hilft uns zu sehen, welche Seiten gelesen werden, damit wir sie verbessern können. Die IP-Adresse wird gekürzt, es findet keine Werbepersonalisierung statt. Wird nur geladen, wenn Sie hier zustimmen.',
    noMarketing:
      'Marketing- oder Werbe-Cookies setzen wir nicht, und wir geben keine Daten zu Werbezwecken weiter.',
    save: 'Speichern',
    cancel: 'Abbrechen',
    saved: 'Ihre Auswahl wurde gespeichert.',
    moreInfo: 'Ausführliche Informationen finden Sie in der',
    and: 'und der',
  },
  en: {
    title: 'Cookie settings',
    intro:
      'Decide what this website may store in your browser. Your choice takes effect immediately and can be changed at any time.',
    sectionTitle: 'What may be stored',
    languageLabel: 'Remember language choice',
    languageHint: 'Stores the language you last chose, so the home page takes you there.',
    themeLabel: 'Remember appearance (light/dark)',
    themeHint: 'Stores whether you prefer the light or the dark view.',
    analyticsLabel: 'Anonymous usage statistics (Google Analytics)',
    analyticsHint:
      'Helps us see which pages get read so we can improve them. IP addresses are truncated and there is no ad personalisation. Loaded only if you consent here.',
    noMarketing:
      'We set no marketing or advertising cookies, and we share no data for advertising purposes.',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Your choice has been saved.',
    moreInfo: 'You will find detailed information in the',
    and: 'and the',
  },
};

export const CookieSettingsPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { cookiePreferences, setCookieConsent } = useCookieConsent();
  const { darkModeEnabled } = useThemeMode();
  const navigate = useNavigate();
  const lp = useLocalizedPath();
  const copy = COPY[language];
  const { cookieBanner } = contentUseCase.getLegalContent(language);

  const [languageEnabled, setLanguageEnabled] = useState(false);
  const [themeEnabled, setThemeEnabled] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLanguageEnabled(cookiePreferences.preferences);
    setThemeEnabled(cookiePreferences.theme ?? false);
    setAnalyticsEnabled(cookiePreferences.analytics ?? false);
  }, [cookiePreferences]);

  const handleSave = () => {
    if (languageEnabled || themeEnabled || analyticsEnabled) {
      setCookieConsent('accepted', {
        preferences: languageEnabled,
        theme: themeEnabled,
        analytics: analyticsEnabled,
      });
    } else {
      // Nothing selected is a decline: it also clears anything stored earlier.
      setCookieConsent('declined');
    }
    setSaved(true);
  };

  return (
    <Box>
      <SEO page="cookies" />

      <PageHeader title={copy.title} subtitle={copy.intro} />

      <Section maxWidth="md">
        <Paper elevation={0} sx={getCardStyles(theme, 'outlined')}>
          <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
            {copy.sectionTitle}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch checked={languageEnabled} onChange={(e) => setLanguageEnabled(e.target.checked)} />
              }
              label={copy.languageLabel}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
              {copy.languageHint}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Only while there is a view to remember. Offering to store a
              preference the site does not act on would be asking for consent
              under a false description, on the one page where that matters
              most. */}
          {darkModeEnabled && (
            <>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={<Switch checked={themeEnabled} onChange={(e) => setThemeEnabled(e.target.checked)} />}
                  label={copy.themeLabel}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                  {copy.themeHint}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={analyticsEnabled}
                  onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  disabled={!isAnalyticsConfigured()}
                />
              }
              label={copy.analyticsLabel}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
              {copy.analyticsHint}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
            {copy.noMarketing}
          </Alert>

          {saved && (
            <Alert severity="success" sx={{ borderRadius: 2, mb: 3 }} role="status">
              {copy.saved}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button onClick={handleSave} sx={getPrimaryButtonStyles(theme)}>
              {copy.save}
            </Button>
            <Button onClick={() => navigate(lp('/'))} sx={getSecondaryButtonStyles(theme)}>
              {copy.cancel}
            </Button>
          </Box>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          {copy.moreInfo}{' '}
          <MuiLink component={Link} to={lp('/cookies')} sx={{ color: 'primary.dark' }}>
            {cookieBanner.cookiePolicy}
          </MuiLink>{' '}
          {copy.and}{' '}
          <MuiLink component={Link} to={lp('/privacy')} sx={{ color: 'primary.dark' }}>
            {cookieBanner.privacyPolicy}
          </MuiLink>
          .
        </Typography>
      </Section>
    </Box>
  );
};
